import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list.
 *
 * Ud over den arvede sideinddeling sørger denne klasse for, at ALLE
 * blogkort i oversigten får samme højde - ikke kun kortene i samme
 * gitter-række (det klarer CSS-gitterets egen "stretch"-adfærd alene),
 * men den højeste af dem alle, uanset hvor mange rækker oversigten
 * har. Det kræver måling med JavaScript, fordi ren CSS ikke kan
 * sammenligne højden på tværs af rækker.
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MutationObserver | undefined} */
  #cardsObserver;

  /** @type {number | undefined} */
  #equalizeRaf;

  connectedCallback() {
    super.connectedCallback();

    const { grid } = this.refs;
    if (!grid) return;

    this.#scheduleEqualizeHeights();

    // Genberegn når kortenes bredde ændrer sig (fx vindue resizes, eller
    // antallet af kolonner pr. række skifter ved et breakpoint) - kortenes
    // naturlige tekst-højde afhænger af deres bredde.
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleEqualizeHeights());
    this.#resizeObserver.observe(this);

    // Genberegn når flere kort tilføjes/fjernes (uendelig scroll/paginering).
    this.#cardsObserver = new MutationObserver(() => this.#scheduleEqualizeHeights());
    this.#cardsObserver.observe(grid, { childList: true });

    // Genberegn når webfonts er færdigindlæst, da tekstens reelle
    // linjeombrydning (og dermed korthøjde) kan ændre sig, når den rigtige
    // skrifttype afløser fallback-fonten.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.#scheduleEqualizeHeights());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#cardsObserver?.disconnect();
    if (this.#equalizeRaf) cancelAnimationFrame(this.#equalizeRaf);
  }

  #scheduleEqualizeHeights() {
    if (this.#equalizeRaf) cancelAnimationFrame(this.#equalizeRaf);
    this.#equalizeRaf = requestAnimationFrame(() => this.#equalizeCardHeights());
  }

  #equalizeCardHeights() {
    /** @type {HTMLElement[]} */
    const cards = Array.from(this.querySelectorAll('.blog-post-card'));
    if (!cards.length) return;

    // Mål hvert korts EGEN naturlige indholdshøjde, uafhængigt af CSS-
    // gitterets "stretch" til rækkens højeste kort (som ellers ville få
    // alle kort i samme række til allerede at vise samme højde, og gøre
    // det umuligt at måle deres reelle indhold). "height: auto" tilsidesætter
    // midlertidigt stylesheet-reglen "height: 100%".
    for (const card of cards) {
      card.style.height = 'auto';
    }

    let maxHeight = 0;
    for (const card of cards) {
      const height = card.offsetHeight;
      if (height > maxHeight) maxHeight = height;
    }

    for (const card of cards) {
      card.style.height = maxHeight > 0 ? `${maxHeight}px` : '';
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
